
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [language, setLanguage] = useState('fr');
  const location = useLocation();

  const navigation = {
    fr: [
      { name: 'Accueil', href: '/' },
      { name: "Offres d'emploi", href: '/emplois' },
      { name: 'Candidats', href: '/candidats' },
      { name: 'Services', href: '/services' },
      { name: 'À propos', href: '/a-propos' },
      { name: 'Contact', href: '/contact' }
    ],
    ar: [
      { name: 'الرئيسية', href: '/' },
      { name: 'فرص العمل', href: '/emplois' },
      { name: 'المرشحون', href: '/candidats' },
      { name: 'الخدمات', href: '/services' },
      { name: 'من نحن', href: '/a-propos' },
      { name: 'اتصل بنا', href: '/contact' }
    ]
  };

  const texts = {
    fr: {
      connect: 'Se connecter',
      register: "S'inscrire",
      subtitle: 'Intérim & Emploi'
    },
    ar: {
      connect: 'تسجيل الدخول',
      register: 'إنشاء حساب',
      subtitle: 'العمل المؤقت والتوظيف'
    }
  };

  const currentTexts = texts[language as keyof typeof texts];
  const isActive = (path: string) => location.pathname === path;

  return (
    <header className="bg-white shadow-lg sticky top-0 z-50">
      <nav className="mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-28">
          {/* Logo */}
          <div className="flex items-center">
            <div className="relative">
              {/* Icône principale avec effet néon */}
              <div className="w-20 h-20 bg-gradient-to-br from-purple-600 via-pink-600 to-red-600 rounded-2xl flex items-center justify-center shadow-2xl transform hover:scale-110 transition-all duration-300 border-4 border-white/20">
                <i className="ri-community-line text-white text-4xl drop-shadow-lg"></i>
                {/* Effet de brillance */}
                <div className="absolute inset-0 bg-gradient-to-tr from-white/30 to-transparent rounded-2xl"></div>
              </div>
              {/* Badge étoile */}
              <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg">
                <i className="ri-star-fill text-white text-sm"></i>
              </div>
            </div>
            
            <div className="ml-6">
              {/* Logo principal ÉNORME */}
              <h1 className="flex items-center gap-4">
                <span 
                  className="text-6xl lg:text-8xl font-black bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 bg-clip-text text-transparent drop-shadow-2xl tracking-tight"
                  style={{ fontFamily: 'Impact, Arial Black, sans-serif' }}
                >
                  Wald Bladi
                </span>
                <span className="text-7xl lg:text-9xl font-black text-yellow-500 drop-shadow-2xl">:</span>
                <span 
                  className="text-5xl lg:text-7xl font-black bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 bg-clip-text text-transparent drop-shadow-2xl"
                  style={{ fontFamily: 'Arial Black, sans-serif' }}
                >
                  ولد بلادي
                </span>
              </h1>
              
              {/* Sous-titre stylé */}
              <div className="flex items-center gap-3 mt-2">
                <p className="text-xl lg:text-2xl font-bold text-gray-700 tracking-wide">
                  {currentTexts.subtitle}
                </p>
                <div className="flex space-x-1">
                  <div className="w-3 h-3 bg-purple-500 rounded-full animate-pulse"></div>
                  <div className="w-3 h-3 bg-pink-500 rounded-full animate-pulse delay-100"></div>
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse delay-200"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navigation[language as keyof typeof navigation].map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`px-4 py-3 text-sm font-bold transition-all whitespace-nowrap cursor-pointer rounded-lg ${
                  isActive(item.href)
                    ? 'text-white bg-gradient-to-r from-orange-500 to-red-500 shadow-lg'
                    : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* Language Switcher & CTA Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            <div className="flex items-center bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl p-1 shadow-inner">
              <button
                onClick={() => setLanguage('fr')}
                className={`px-4 py-2 text-sm font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap ${
                  language === 'fr' ? 'bg-white text-orange-600 shadow-lg transform scale-105' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                FR
              </button>
              <button
                onClick={() => setLanguage('ar')}
                className={`px-4 py-2 text-sm font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap ${
                  language === 'ar' ? 'bg-white text-emerald-600 shadow-lg transform scale-105' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                العربية
              </button>
            </div>
            <Link
              to="/connexion"
              className="text-gray-700 hover:text-orange-600 font-bold cursor-pointer whitespace-nowrap px-4 py-2 rounded-lg hover:bg-orange-50 transition-all"
            >
              {currentTexts.connect}
            </Link>
            <Link
              to="/inscription"
              className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-3 rounded-xl hover:from-orange-600 hover:to-red-600 transition-all font-bold cursor-pointer whitespace-nowrap shadow-xl transform hover:scale-105"
            >
              {currentTexts.register}
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center space-x-2">
            <button
              onClick={() => setLanguage(language === 'fr' ? 'ar' : 'fr')}
              className="text-gray-700 hover:text-orange-600 cursor-pointer px-3 py-2 text-sm font-bold rounded-lg hover:bg-orange-50"
            >
              {language === 'fr' ? 'العربية' : 'FR'}
            </button>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-700 hover:text-orange-600 cursor-pointer p-2 rounded-lg hover:bg-orange-50"
            >
              <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-2xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-6 border-t border-gray-200">
            <div className="flex flex-col space-y-3">
              {navigation[language as keyof typeof navigation].map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`px-4 py-3 text-sm font-bold cursor-pointer rounded-lg transition-all ${
                    isActive(item.href)
                      ? 'text-white bg-gradient-to-r from-orange-500 to-red-500 shadow-lg'
                      : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              <div className="pt-6 border-t border-gray-200 space-y-3">
                <Link
                  to="/connexion"
                  className="block px-4 py-3 text-sm font-bold text-gray-700 hover:text-orange-600 cursor-pointer rounded-lg hover:bg-orange-50"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {currentTexts.connect}
                </Link>
                <Link
                  to="/inscription"
                  className="block px-4 py-3 text-sm font-bold bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 transition-all cursor-pointer shadow-lg"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {currentTexts.register}
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
